import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import EfVideoMedia from '../ef-video-media';
import EFPicture from '../ef-picture';
import EfLink from '../ef-link';
import './index.scss';

interface EfSectionMediaProps {
    /**
     * 资源链接是否是视频
     */
    isVideo?: boolean;
    /**
     * 所有分辨率展示的资源链接
     */
    pc: string;
    /**
     * pad端展示的资源链接
     */
    pad?: string;
    /**
     * M端/pad端展示的资源链接
     */
    h5?: string;
    /**
     * 视频贴图-所有分辨率
     */
    poster?: string;
    /**
     * 视频贴图-Pad
     */
    posterPad?: string;
    /**
     * 视频贴图-Mobile
     */
    posterH5?: string;

    /**
     * 主题色
     */
    theme?: 'black' | 'white';

    /**
     * 是否开启进场动画
     */
    hasAnimation?: boolean;

    /**
     *
     * 进场动画是否已启动
     */
    animationEnterCallback?: () => void;

    /**
     * 文案展示位置
     */
    position?: 'default' | 'hover';

    /**
     * label(处于标题之上的叫label)
     */
    label?: string;
    /**
     * 主标题
     */
    title?: string;
    /**
     * 副标题
     */
    subTitle?: string;
    /**
     * 描述
     */
    desc?: string;

    /**
     * 样式名
     */
    className?: string;

    /**
     * 更多详情-链接
     */
    moreLink?: string;
    /**
     * 更多详情-文案
     */
    moreText?: string;

    /**
     * 购买按钮-链接
     */
    buyLink?: string;
    /**
     * 购买按钮-文案
     */
    buyText?: string;
}

/**
 * 维护: kalkin
 *
 * 段落 - 768可以兼容使用pc的图/视频
 */
const EfSectionMedia: React.FC<EfSectionMediaProps> = ({
    isVideo,
    pc,
    pad,
    h5,
    poster,
    posterPad,
    posterH5,
    theme = 'black',
    position,
    hasAnimation = true,
    animationEnterCallback,
    label,
    title,
    subTitle,
    desc,
    className,
    moreLink,
    moreText,
    buyLink,
    buyText,
}) => {
    const refSection = useRef(null);
    const [isEnter, setIsEnter] = useState(false);
    const prefixCls = `ef-section-media`;
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-position-${position}`]: position,
        [`${prefixCls}-theme-${theme}`]: theme,
        [`${prefixCls}-animation`]: hasAnimation,
        [`${prefixCls}-enter`]: isEnter,
    });
    // 是否有按钮
    const hasBtn = (moreLink && moreText) || (buyLink && buyText);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (elems: any) => {
                const elem = elems[0];

                const topNum = elem.boundingClientRect.top;

                if (elem.isIntersecting && topNum > 0) {
                    setIsEnter(true);
                    animationEnterCallback && animationEnterCallback();
                }
            },
            {
                rootMargin: `0px 0px -100px 0px`,
                threshold: 0,
            },
        );
        observer.observe(refSection.current as any);
    }, []);

    return (
        <div className={classes} ref={refSection}>
            <div className={`${prefixCls}-info`}>
                <div className={`${prefixCls}-msg`}>
                    {label && (
                        <p
                            className={`${prefixCls}-label`}
                            dangerouslySetInnerHTML={{
                                __html: label,
                            }}
                        ></p>
                    )}
                    {title && (
                        <h2
                            className={`${prefixCls}-title`}
                            dangerouslySetInnerHTML={{
                                __html: title,
                            }}
                        ></h2>
                    )}
                    {subTitle && (
                        <p
                            className={`${prefixCls}-subTitle`}
                            dangerouslySetInnerHTML={{
                                __html: subTitle,
                            }}
                        ></p>
                    )}
                    {desc && (
                        <div
                            className={`${prefixCls}-desc`}
                            dangerouslySetInnerHTML={{
                                __html: desc,
                            }}
                        ></div>
                    )}
                </div>
                {hasBtn && (
                    <div className={`${prefixCls}-btns`}>
                        {moreLink && moreText && (
                            <EfLink href={moreLink} className={`${prefixCls}-link_more`}>
                                {moreText}
                            </EfLink>
                        )}
                        {buyLink && buyText && (
                            <EfLink href={buyLink} className={`${prefixCls}-buy_now`}>
                                {buyText}
                            </EfLink>
                        )}
                    </div>
                )}
            </div>
            {pc && (
                <div className={`${prefixCls}-img`}>
                    {isVideo ? <EfVideoMedia pc={pc} pad={pad || h5 || pc} h5={h5 || pc} poster={poster} posterH5={posterH5 || poster} autoPlay={true} loop={true} /> : <EFPicture src={pc} srcPad={pad || h5 || pc} srcMobile={h5 || pc} /> }
                </div>
            )}
        </div>
    );
};

export default EfSectionMedia;
